import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import { type CSSProperties, forwardRef, type HTMLAttributes, type ReactElement, type ReactNode, useRef } from "react";
import { type AriaSearchFieldProps, mergeProps, useComboBox, useSearchField, VisuallyHidden } from "react-aria";
import { type ComboBoxStateOptions, useComboBoxState, useSearchFieldState } from "react-stately";

import { useDOMRef, useHotKeys } from "../../hooks.ts";
import formStyle from "../../styles/form.module.css";
import { Badge } from "../Badge/Badge.tsx";
import styleMenu from "../ContextualMenu/ContextualMenu.module.css";
import { Description } from "../Description/Description.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import { Label } from "../Label/Label.tsx";
import { ListBox } from "../List/ListBox.tsx";
import { Popover } from "../Popover/Popover.tsx";
import { LoadingSpinner } from "../ProgressSpinner/LoadingSpinner.tsx";
import { Shortcut } from "../Shortcut/Shortcut.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";
import style from "../TextField/TextField.module.css";
import { TriggerButton } from "../TriggerButton/TriggerButton.tsx";

export interface SearchAutosuggestProps extends ComboBoxStateOptions<object> {
  style?: CSSProperties;
  icon?: ReactElement | MaterialSymbol;
  className?: string;
  width?: number;
  isLoading?: boolean;
  domProps?: HTMLAttributes<HTMLInputElement>;
  focusShortcut?: string;
}

// @see: https://react-spectrum.adobe.com/react-stately/collections.html

export const SearchAutosuggest = forwardRef<HTMLInputElement, SearchAutosuggestProps>(function ComboBox(props, ref) {
  // Use for Popover position
  const boxRef = useRef<HTMLDivElement>(null);

  const inputRef = useDOMRef(ref);
  const listBoxRef = useRef(null);
  const popoverRef = useRef(null);

  // ComboBox
  // let { contains } = useFilter({ sensitivity: "base" });
  // const state = useComboBoxState({ ...props, defaultFilter: contains });
  const state = useComboBoxState(props);
  const { inputProps, listBoxProps, labelProps, descriptionProps, errorMessageProps } = useComboBox(
    {
      ...props,
      label: props.label ?? props.placeholder,
      inputRef,
      listBoxRef,
      popoverRef,
    },
    state,
  );

  // Get props for the clear button from useSearchField
  const searchProps = {
    label: props.label ?? props.placeholder,
    value: state.inputValue,
    onChange: (v: string) => {
      state.setInputValue(v);
    },
    onClear() {
      setTimeout(() => {
        state.setFocused(true);
      }, 300); // Well, a trick to bring back focus
    },
  } satisfies AriaSearchFieldProps;

  const searchState = useSearchFieldState(searchProps);
  const { clearButtonProps } = useSearchField(searchProps, searchState, inputRef);

  useHotKeys(props.focusShortcut ?? "", () => {
    inputRef.current?.focus();
  });

  return (
    <>
      <VStack spacing={2} style={{ maxWidth: "100%" }}>
        {!!props.label && (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label {...labelProps}>{props.label}</Label>
            <Spacer />
            {!!props.isReadOnly && <Badge intent="light" large inline label="read only" style={{ flex: "0 0 auto" }} />}
            {!props.isRequired && !props.isReadOnly && (
              <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />
            )}
          </HStack>
        )}
        <Description
          {...descriptionProps}
          data-testid="text-field-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: props.label != undefined && (props.isReadOnly || !props.isRequired),
          })}
        >
          {props.description}
        </Description>
        <HStack
          spacing={3}
          className={clsx(style.input_group, props.className, {
            [style.simple]: !props.label,
            [style.disabled]: props.isDisabled,
            [style.focus]: state.isFocused,
            [style.error]: props.isInvalid,
          })}
          style={{ width: props.width, maxWidth: "100%", ...props.style }}
          data-testid="search-autosuggest"
          ref={boxRef}
        >
          {!props.label && (
            <VisuallyHidden>
              <label {...labelProps}>{props.placeholder}</label>
            </VisuallyHidden>
          )}
          {!!props.icon && <Icon icon={props.icon} className={style.icon} />}
          <input {...mergeProps(inputProps, props.domProps ?? {})} className={style.input} ref={inputRef} />
          <div className={style.action}>
            {!!props.isLoading && <LoadingSpinner size={16} style={{ marginRight: 3 }} />}
            {!props.isDisabled && !!state.inputValue && (
              <TriggerButton
                icon={<SymbolIcon icon="cancel" filled size={16} style={{ color: "var(--form-placeholder)" }} />}
                label={""}
                {...clearButtonProps}
                data-e2e-clear
              />
            )}
            {!!props.focusShortcut && (
              <Shortcut
                label={props.focusShortcut}
                style={{ display: state.isFocused || state.inputValue ? "none" : undefined, marginRight: 3 }}
              />
            )}
          </div>
        </HStack>
        {!!props.errorMessage && !!props.isInvalid && !props.isDisabled && (
          <div role="alert" {...errorMessageProps} className={formStyle.error_message} data-testid="number-field-error">
            {props.errorMessage as ReactNode}
          </div>
        )}
      </VStack>
      {!!state.collection.getFirstKey() && (
        <Popover ref={popoverRef} state={state} triggerRef={boxRef} placement={"bottom start"} isNonModal offset={5}>
          <ListBox
            {...listBoxProps}
            listBoxRef={listBoxRef}
            state={state}
            style={{ minWidth: props.width }}
            className={styleMenu.menu}
          />
        </Popover>
      )}
    </>
  );
});
